import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface AuthContextProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  getCookie: any;
  setCookie: any;
  removeCookie: any;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const apiToken = getCookie('apiToken');
    if (apiToken) {
      fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${apiToken}` },
      })
        .then((res) => res.ok ? res.json() : null)
        .then((userData) => {
          if (userData) setUser(userData);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post('https://develop.coursemos.co.kr/api/v1/login', new URLSearchParams({
        email,
        password,
      }).toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.token) {
        setCookie('userId', response.data.user.id);

        const userData = await createOrUpdateUser(response.data.user);
        if (userData) {
          setUser(userData);
          setCookie('apiToken', userData.api_token);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Login failed', error);
      return false;
    }
  };

  const createOrUpdateUser = async (data: any): Promise<any> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({user: data})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.user;
    } catch (error) {
      removeCookie('apiToken');
      removeCookie('userId');
      console.error(error);
      return null;
    }
  };

  const refreshUser = async () => {
    const apiToken = getCookie('apiToken');
    if (!apiToken) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${apiToken}` },
      });
      if (res.ok) setUser(await res.json());
    } catch {}
  };

  const logout = () => {
    removeCookie('apiToken');
    removeCookie('userId');
    setUser(null);
    navigate('/login');
  };

  return (
      <AuthContext.Provider value={{ isAuthenticated: !!user, isAdmin: !!user?.is_admin, user, loading, login, logout, refreshUser, getCookie, setCookie, removeCookie }}>
        {children}
      </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Utility functions to handle cookies
export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
};

export const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${value}; path=/; max-age=2592000`; // 30 days
};

export const removeCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
};
